import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";

/**
 * Tracks JSON validity across every {@link JsonField} on a page so a single Save
 * button can block while any raw-JSON panel is malformed. Sections don't need to
 * thread validity props up manually — each field self-registers here.
 */
interface JsonValidityContextValue {
  setValidity: (id: string, valid: boolean) => void;
  clear: (id: string) => void;
}

const JsonValidityContext = createContext<JsonValidityContextValue | null>(null);

/** Wraps an editor so `useJsonValidity()` can report whether any JSON panel is invalid. */
export const JsonValidityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const setValidity = useCallback((id: string, valid: boolean) => {
    setErrors((prev) => {
      const isError = !valid;
      if (Boolean(prev[id]) === isError) return prev;
      const next = { ...prev };
      if (isError) next[id] = true;
      else delete next[id];
      return next;
    });
  }, []);

  const clear = useCallback((id: string) => {
    setErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const hasErrors = Object.keys(errors).length > 0;
  const ctx = useMemo(() => ({ setValidity, clear }), [setValidity, clear]);

  return (
    <JsonValidityContext.Provider value={ctx}>
      <ValidityStateContext.Provider value={hasErrors}>{children}</ValidityStateContext.Provider>
    </JsonValidityContext.Provider>
  );
};

const ValidityStateContext = createContext<boolean>(false);

/** True when any {@link JsonField} under the nearest {@link JsonValidityProvider} holds invalid JSON. */
export const useJsonValidity = (): boolean => useContext(ValidityStateContext);

const pretty = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
};

interface JsonFieldProps {
  label?: string;
  /** The object/array being edited. Structured edits elsewhere flow back in here. */
  value: unknown;
  /** Called with the parsed value whenever the text is valid JSON. */
  onChange: (parsed: any) => void;
  onValidityChange?: (valid: boolean) => void;
  helperText?: string;
  minRows?: number;
  /** Value used when the field is cleared to empty (default `{}`). */
  emptyValue?: any;
  /** Viewable and copyable, but not editable. */
  readOnly?: boolean;
}

/**
 * Controlled raw-JSON editor. Keeps a local text draft so a half-typed value
 * isn't clobbered by re-renders, resyncs when the external value changes from an
 * outside edit, validates on every keystroke, and only emits parsed values while
 * the text is valid. Invalid JSON surfaces an error and (via context) blocks Save.
 */
const JsonField: React.FC<JsonFieldProps> = ({ label, value, onChange, onValidityChange, helperText, minRows = 4, emptyValue = {}, readOnly = false }) => {
  const id = useId();
  const validity = useContext(JsonValidityContext);
  const [text, setText] = useState<string>(() => pretty(value));
  const [error, setError] = useState<string | null>(null);
  // Remember the last value we emitted so an incoming prop change from *our own*
  // edit doesn't fight the user's in-progress text (only external edits resync).
  const lastEmitted = useRef<unknown>(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const incoming = pretty(value);
    // Skip resync if the current text already parses to the same value.
    try {
      if (text.trim() && JSON.stringify(JSON.parse(text)) === JSON.stringify(value)) {
        lastEmitted.current = value;
        return;
      }
    } catch {
      /* fall through to resync */
    }
    setText(incoming);
    setError(null);
    lastEmitted.current = value;
    validity?.setValidity(id, true);
    onValidityChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => () => validity?.clear(id), [validity, id]);

  const emit = (raw: string) => {
    setText(raw);
    if (!raw.trim()) {
      setError(null);
      lastEmitted.current = emptyValue;
      validity?.setValidity(id, true);
      onValidityChange?.(true);
      onChange(emptyValue);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setError(null);
      lastEmitted.current = parsed;
      validity?.setValidity(id, true);
      onValidityChange?.(true);
      onChange(parsed);
    } catch (e: any) {
      setError(e?.message || "Invalid JSON");
      validity?.setValidity(id, false);
      onValidityChange?.(false);
    }
  };

  const format = () => {
    try {
      setText(pretty(JSON.parse(text)));
      setError(null);
      validity?.setValidity(id, true);
    } catch (e: any) {
      setError(e?.message || "Invalid JSON");
    }
  };

  return (
    <Box>
      {label && (
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: "4px" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>{label}</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<AutoFixHighOutlinedIcon fontSize="small" />} onClick={format} disabled={!text.trim()}>Format</Button>
          )}
        </Stack>
      )}
      <TextField
        fullWidth
        multiline
        minRows={minRows}
        value={text}
        onChange={(e) => emit(e.target.value)}
        error={Boolean(error)}
        helperText={error || helperText}
        InputProps={{ readOnly, sx: { fontFamily: "monospace", fontSize: "0.8rem" } }}
      />
    </Box>
  );
};

export default JsonField;
